import { useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Zap } from "lucide-react";

/**
 * Intermediate confirmation page to avoid link prefetch consumption.
 * Email scanners prefetch links and consume the one-time token. This page shows
 * a "Confirm" button; only when the user clicks do we call verifyOtp.
 * We use verifyOtp (client API) instead of redirecting to Supabase's GET /verify
 * to avoid 500 errors that can occur with database triggers or server-side verify.
 */
export default function AuthConfirmPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  /** Supabase may use + in base64; some clients strip + in URLs — normalize before verify */
  const rawHash = searchParams.get("token_hash");
  const tokenHash = rawHash ? rawHash.replace(/ /g, "+") : null;
  const typeParam = searchParams.get("type") || "email";
  const type = typeParam as "email" | "signup" | "recovery" | "invite" | "magiclink";

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!tokenHash) {
    navigate("/login?error=auth_callback_failed", { replace: true });
    return null;
  }

  const handleConfirm = async () => {
    setLoading(true);
    setError(null);
    // For Supabase, the stable verification approach for "signup/magiclink-style" email links is `type: "email"`.
    // We'll only retry with legacy `signup` when we specifically hit the password-required error.
    const primaryVerifyType = (type === "signup" || type === "magiclink" ? "email" : type) as typeof type;
    const legacyRetryVerifyType = (type === "signup" ? "signup" : "magiclink") as typeof type;

    let lastErrMessage: string | undefined;

    const { error: primaryErr } = await supabase.auth.verifyOtp({
      token_hash: tokenHash,
      type: primaryVerifyType,
    });

    if (!primaryErr) {
      setLoading(false);
      // success path continues below
    } else {
      lastErrMessage = primaryErr.message || String(primaryErr);
      const msg = lastErrMessage.toLowerCase();

      const shouldRetryLegacySignup =
        type === "signup" && msg.includes("signup requires a valid password") && legacyRetryVerifyType !== primaryVerifyType;

      if (shouldRetryLegacySignup) {
        const { error: legacyErr } = await supabase.auth.verifyOtp({
          token_hash: tokenHash,
          type: legacyRetryVerifyType,
        });
        if (legacyErr) {
          lastErrMessage = legacyErr.message || String(legacyErr);
        } else {
          setLoading(false);
        }
      }

      if (lastErrMessage) {
        setLoading(false);
        setError(lastErrMessage);
        return;
      }
    }
    setLoading(false);
    if (type === "recovery") {
      window.location.replace(window.location.origin + "/login?mode=recovery");
      return;
    }
    window.location.replace(window.location.origin + "/");
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-foreground p-4">
      <div className="w-full max-w-md rounded-lg border border-border bg-card p-8 text-center shadow-sm">
        <div className="mx-auto mb-4 flex items-center justify-center gap-2">
          <Zap className="h-8 w-8 text-primary" />
          <span className="text-xl font-bold">ReviewBoost Pro Manager</span>
        </div>
        <h1 className="text-lg font-semibold">
          {type === "recovery"
            ? "Reset your password"
            : type === "invite" || type === "magiclink" || type === "signup"
              ? "Accept invitation"
              : "Confirm your email"}
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">
          {type === "recovery"
            ? "Click the button below to open the secure new-password screen."
            : type === "invite" || type === "magiclink" || type === "signup"
              ? "Click the button below to accept the invitation and join the team."
              : "Click the button below to verify your account and sign in."}
        </p>
        {error && (
          <p className="mt-4 text-sm text-destructive">{error}</p>
        )}
        <Button
          className="mt-6 w-full bg-primary text-primary-foreground hover:bg-primary/90"
          onClick={handleConfirm}
          disabled={loading}
        >
          {loading
            ? "Verifying…"
            : type === "recovery"
              ? "Continue to new password"
              : type === "invite" || type === "magiclink" || type === "signup"
                ? "Accept & sign in"
                : "Confirm & sign in"}
        </Button>
        <p className="mt-4 text-xs text-muted-foreground">
          <Link to="/login" className="text-primary hover:underline">
            Back to sign in
          </Link>
        </p>
      </div>
    </div>
  );
}
